import { useState } from 'react';
import {
  List,
  Avatar,
  Input,
  Space,
  Typography,
  Card,
  Empty,
  Spin,
  Tag,
  Row,
  Col,
  Badge,
} from 'antd';
import {
  UserOutlined,
  SearchOutlined,
  MessageOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { useCompany } from '../../hooks/company';
import {
  getUserSmsAddresses,
  getUserSmsMessages,
  UserSmsDTO,
} from '../../apis/api';
import { ListUserItemDTO } from '../../api';
import dayjs from 'dayjs';
import AuditUserList from '../../components/AuditUserList';
import './audit.scss';

const { Text } = Typography;
const { Search } = Input;

export default function SmsManage() {
  const company = useCompany();
  const [selectedUser, setSelectedUser] = useState<ListUserItemDTO | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);

  // 获取短信地址列表
  const {
    data: addresses,
    loading: loadingAddresses,
    run: loadAddresses,
  } = useRequest(
    async (userId: number) => {
      if (!company?.id) return [];
      return await getUserSmsAddresses(company.id, userId);
    },
    {
      manual: true,
    },
  );

  // 获取短信消息
  const {
    data: messages,
    loading: loadingMessages,
    run: loadMessages,
  } = useRequest(
    async (userId: number, address: string) => {
      if (!company?.id) return [];
      return await getUserSmsMessages(company.id, userId, address);
    },
    {
      manual: true,
    },
  );

  const handleUserClick = (user: ListUserItemDTO) => {
    setSelectedUser(user);
    setSelectedAddress(null);
    loadAddresses(user.id as number);
  };

  const handleAddressClick = (address: string) => {
    setSelectedAddress(address);
    if (selectedUser) {
      loadMessages(selectedUser.id as number, address);
    }
  };

  return (
    <div className="audit-manage-container">
      <Row gutter={16} style={{ height: '100%' }}>
        {/* A侧：用户列表 */}
        <Col span={6} className="audit-left-panel">
          <AuditUserList
            selectedUserId={selectedUser?.id ? Number(selectedUser.id) : undefined}
            onUserSelect={handleUserClick}
          />
        </Col>

        {/* B侧：地址列表和短信对话 */}
        <Col span={18}>
          {!selectedUser ? (
            <div className="audit-empty-state">
              <Empty description="请选择左侧人员查看短信记录" />
            </div>
          ) : (
            <Row gutter={16} style={{ height: '100%' }}>
              {/* 地址列表 */}
              <Col span={8} className="audit-middle-panel">
                <Card
                  title={
                    <Space>
                      <PhoneOutlined />
                      <span>短信地址</span>
                    </Space>
                  }
                  className="audit-card"
                  bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                  <div className="audit-list-container" style={{ flex: 1, overflow: 'hidden' }}>
                    <Spin spinning={loadingAddresses}>
                      {!addresses || addresses.length === 0 ? (
                        <Empty
                          description="暂无短信地址"
                          style={{ marginTop: '50%' }}
                        />
                      ) : (
                        <List
                          dataSource={addresses}
                          renderItem={(address) => (
                            <List.Item
                              className={`audit-list-item ${
                                selectedAddress === address ? 'active' : ''
                              }`}
                              onClick={() => handleAddressClick(address)}
                            >
                              <List.Item.Meta
                                avatar={
                                  <Avatar
                                    style={{
                                      backgroundColor: '#1890ff',
                                    }}
                                  >
                                    {address.charAt(0)}
                                  </Avatar>
                                }
                                title={address}
                                description="点击查看短信记录"
                              />
                            </List.Item>
                          )}
                        />
                      )}
                    </Spin>
                  </div>
                </Card>
              </Col>

              {/* 短信对话 */}
              <Col span={16} className="audit-right-panel">
                <Card
                  title={
                    <Space>
                      <MessageOutlined />
                      <span>
                        {selectedAddress
                          ? `与 ${selectedAddress} 的短信记录`
                          : '短信记录'}
                      </span>
                    </Space>
                  }
                  className="audit-card"
                  bodyStyle={{
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <Spin spinning={loadingMessages}>
                    {!selectedAddress ? (
                      <Empty description="请选择左侧地址查看短信记录" />
                    ) : !messages || messages.length === 0 ? (
                      <Empty description="暂无短信记录" />
                    ) : (
                      <div className="message-list">
                        {messages.map((msg: UserSmsDTO) => (
                          <div
                            key={msg.id}
                            className={`message-item ${
                              msg.type === 2 ? 'message-send' : 'message-receive'
                            }`}
                          >
                            <div className="message-info">
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {msg.type === 2 ? '发送' : '接收'} •{' '}
                                {dayjs(msg.smsedAt).format(
                                  'YYYY-MM-DD HH:mm:ss',
                                )}
                              </Text>
                            </div>
                            <div className="message-bubble">
                              <Text>{msg.body}</Text>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Spin>
                </Card>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
}

